import axios from 'axios';

const API_BASE_URL = 'https://in6zkeyaof.execute-api.us-east-1.amazonaws.com/dev';

function api(token?: string) {
  return axios.create({
    baseURL: API_BASE_URL,
    headers: token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {},
  });
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BenchmarkTestMeta {
  name: string;
  entry_count: number;
  account_count: number;
  accounts: string[];
  captured_at?: string;
}

export interface BankBenchmarkTestMeta {
  name: string;
  transaction_count: number;
  statement_count: number;
  bank_name?: string;
  account_owner?: string;
  captured_at?: string;
}

export interface TestMetrics {
  precision: number;
  recall: number;
  f1: number;
  true_positives: number;
  false_positives: number;
  false_negatives: number;
  golden_count: number;
  extracted_count: number;
  optional_field_coverage?: Record<string, number>;
  // GL specific
  sample_unmatched_golden?: Array<{
    entry_date: string;
    account_name: string;
    debit: string;
    credit: string;
  }>;
  // Bank specific
  abs_tp?: number;
  dir_tp?: number;
  exact_tp?: number;
  statement_count_correct?: boolean;
  balance_accuracy?: number;
  bank_name_found?: boolean;
  account_owner_found?: boolean;
  sample_unmatched_golden_bank?: Array<{
    date: string;
    type: string;
    amount: number;
    description: string;
  }>;
}

export interface TestResult {
  test_name: string;
  status: 'success' | 'error' | 'dry_run';
  f1?: number;
  recall?: number;
  precision?: number;
  extracted?: number;
  golden?: number;
  seconds?: number;
  error?: string;
  metrics?: TestMetrics;
  golden_entry_count?: number;
  extracted_entry_count?: number;
  extraction_seconds?: number;
  source_file?: string;
  run_at?: string;
}

export interface BenchmarkRunSummary {
  run_at: string;
  run_prefix: string;
  tests_run: number;
  tests_passed: number;
  tests_failed: number;
  overall_f1: number | null;
  overall_recall: number | null;
  total_extraction_seconds: number;
  results: TestResult[];
  _s3_key?: string;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

export const fetchBenchmarkRuns = async (token?: string, limit = 25) => {
  if (!token) throw new Error('No auth token');
  const resp = await api(token).get<{ runs: BenchmarkRunSummary[]; count: number }>(
    `/admin/benchmarks/runs?limit=${limit}`
  );
  return resp.data;
};

export const fetchBenchmarkTests = async (token?: string) => {
  if (!token) throw new Error('No auth token');
  const resp = await api(token).get<{ tests: BenchmarkTestMeta[]; count: number }>(
    '/admin/benchmarks/tests'
  );
  return resp.data;
};

export const triggerBenchmarkRun = async (
  opts: { test_names?: string[]; dry_run?: boolean } = {},
  token?: string
) => {
  if (!token) throw new Error('No auth token');
  const resp = await api(token).post<{ message: string; test_names: string[] | 'all'; dry_run: boolean }>(
    '/admin/benchmarks/runs',
    opts
  );
  return resp.data;
};

// ---------------------------------------------------------------------------
// Bank statement benchmark API calls
// ---------------------------------------------------------------------------

export const fetchBankBenchmarkRuns = async (token?: string, limit = 25) => {
  if (!token) throw new Error('No auth token');
  const resp = await api(token).get<{ runs: BenchmarkRunSummary[]; count: number }>(
    `/admin/benchmarks/runs?type=bank&limit=${limit}`
  );
  return resp.data;
};

export const fetchBankBenchmarkTests = async (token?: string) => {
  if (!token) throw new Error('No auth token');
  const resp = await api(token).get<{ tests: BankBenchmarkTestMeta[]; count: number }>(
    '/admin/benchmarks/tests?type=bank'
  );
  return resp.data;
};

export const triggerBankBenchmarkRun = async (
  opts: { test_names?: string[]; dry_run?: boolean } = {},
  token?: string
) => {
  if (!token) throw new Error('No auth token');
  const resp = await api(token).post<{ message: string; test_names: string[] | 'all'; dry_run: boolean }>(
    '/admin/benchmarks/runs',
    { ...opts, type: 'bank' }
  );
  return resp.data;
};
